import React from 'react';
import { useNavigate } from 'react-router-dom';

const LAST_UPDATED = 'May 2025';

const Card = ({ children, style = {} }) => (
  <div style={{ background: '#ffffff', border: '1px solid #E5E9F0', borderRadius: '1rem', padding: '1.75rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', ...style }}>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h2 style={{ color: '#0D1F3C', fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.75rem' }}>{title}</h2>
    <div style={{ color: '#374151', lineHeight: 1.75, fontSize: '0.9375rem' }}>{children}</div>
  </div>
);

const P = ({ children }) => <p style={{ marginBottom: '0.875rem' }}>{children}</p>;

const BulletList = ({ items }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 1rem' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
        <span style={{ color: '#1A6FD6', fontWeight: 700, flexShrink: 0, marginTop: '0.1rem' }}>—</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ title, children, type = 'info' }) => {
  const styles = {
    info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ', iconColor: '#1A6FD6' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', icon: '✓', iconColor: '#16A34A' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', icon: '!', iconColor: '#D97706' },
  };
  const s = styles[type];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <span style={{ color: s.iconColor, fontWeight: 700, fontSize: '1rem', flexShrink: 0, marginTop: '0.1rem' }}>{s.icon}</span>
        <div>
          {title && <p style={{ fontWeight: 700, color: '#0D1F3C', marginBottom: '0.25rem' }}>{title}</p>}
          <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: 1.6 }}>{children}</p>
        </div>
      </div>
    </div>
  );
};

const ShippingPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#FFFBEB', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      <div style={{ background: '#ffffff', borderBottom: '1px solid #E5E9F0', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontWeight: 600, fontSize: '0.875rem' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back to Successlane
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1A6FD6' }}></div>
            <span style={{ fontWeight: 700, color: '#0D1F3C', fontSize: '0.9rem' }}>Successlane</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>

        <div style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1A6FD6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Legal</p>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#0D1F3C', marginBottom: '0.5rem', lineHeight: 1.2 }}>Shipping &amp; Service Delivery Policy</h1>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Operated by <strong>Success Lane</strong></p>
        </div>

        <Card>
          <Highlight type="info" title="Successlane is a 100% digital service">
            We do not ship any physical goods. All deliverables are sent electronically as a PDF document to the email address you provide at checkout, typically within 48 hours of successful payment.
          </Highlight>

          <Section title="1. What you receive">
            <P>For every paid order, you receive the following digital deliverables:</P>
            <BulletList items={[
              'Your fully rewritten LinkedIn profile — headline, summary, and experience sections — as a single PDF document.',
              'The recruiter certification — including the certifying recruiter\'s name, current employer, domain, and years of experience — embedded inside the same PDF.',
              'The personalised LinkedIn Playbook covering keyword placement, organic visibility tactics, headline/summary templates, and proven outreach scripts.',
            ]} />
          </Section>

          <Section title="2. Delivery timeline">
            <P>We aim to deliver every order within <strong>48 hours of successful payment</strong>, provided you have submitted your intake form in full. The clock starts when both of the following have happened:</P>
            <BulletList items={[
              'Your payment has been confirmed by our payment partner Razorpay.',
              'You have submitted a complete intake form with the information we need to write your rewrite (target role, dream companies, career highlights, etc.).',
            ]} />
            <P>If the intake form is incomplete, the 48-hour clock starts only after we have received the missing information. We will reach out by email or WhatsApp to request anything we need.</P>
          </Section>

          <Section title="3. How and where it is delivered">
            <BulletList items={[
              'Channel: Email, sent to the address you provided at checkout.',
              'Backup channel: WhatsApp on the registered number, if email delivery fails.',
              'Format: A single PDF document containing the rewrite, the recruiter certification, and the LinkedIn Playbook.',
              'Geographic scope: Worldwide — because delivery is digital, your location does not affect delivery time or cost.',
              'Delivery cost: Zero. There are no shipping or handling charges of any kind.',
            ]} />
          </Section>

          <Section title="4. If you don’t receive your delivery">
            <P>If 48 hours have passed since you submitted a complete intake form and you have not received your PDF, please:</P>
            <BulletList items={[
              'Check your spam, promotions, or junk folders for an email from contact@successlane.io.',
              'Confirm that the email address used at checkout is correct and active.',
              'If the document is still missing, write to contact@successlane.io or call +91 72594 44336.',
            ]} />
            <P>We will re-send the deliverable immediately, and where the delay was on our end, we will let you know what happened and how we are preventing it from happening again.</P>
          </Section>

          <Section title="5. After delivery — using your document">
            <P>The delivered PDF is yours to keep and use for your personal career development. You can:</P>
            <BulletList items={[
              'Paste the rewritten content directly into the relevant sections of your live LinkedIn profile.',
              'Save and re-download the PDF whenever needed.',
              'Email or call our support team for any clarification on how to implement specific sections of the rewrite or Playbook.',
            ]} />
          </Section>

          <Section title="6. Peak periods and exceptions">
            <P>During occasional high-demand periods (festive seasons, product launches), delivery may extend beyond 48 hours. In any such case, we will <strong>proactively</strong> communicate the revised timeline via email or WhatsApp before your expected delivery window passes.</P>
          </Section>

          <Section title="7. Support">
            <P>For anything related to delivery — missing files, wrong email, formatting issues, or implementation help — reach us at <a href="mailto:contact@successlane.io" style={{ color: '#1A6FD6' }}>contact@successlane.io</a> or <a href="tel:+917259444336" style={{ color: '#1A6FD6' }}>+91 72594 44336</a>. Working hours: Monday to Saturday, 9:00 AM – 6:00 PM IST.</P>
          </Section>
        </Card>

        <div style={{ borderTop: '1px solid #E5E9F0', paddingTop: '2rem', marginTop: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ color: '#9CA3AF', fontSize: '0.8125rem' }}>© 2025 Success Lane · Successlane</p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/refund')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Refund</button>
            <button onClick={() => navigate('/cancellation')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Cancellation</button>
            <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A6FD6', fontSize: '0.8125rem', fontWeight: 500 }}>Terms</button>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '0.8125rem' }}>Back to Home</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
